import { Component, OnInit, AfterViewInit, ViewChild, inject, OnDestroy, signal, computed, output } from '@angular/core';
import { Router } from '@angular/router';
import { PrimeNgDataTableComponent } from '../../../shared/components/prime-ng-data-table';
import { DatatableColumn, DatatableOptions, DatatableEvent, LazyLoadEvent, DataFilterConfig, LabelFilterConfig, TextFilterConfig } from '../../../core/models/datatable.models';
import { RoleService } from '../../../core/services/role.service';
import { PaginatedRolesRequest, PaginatedRolesResponse, DeleteRoleResponse } from '../../../core/models/role.models';
import { PermissionService } from '../../../core/services/permission.service';
import { SYSTEM_PERMISSION_LIST } from '../../../shared/constants/permissions.constants';

import { buildRouteWithTenant } from '../../../shared/utils/route.util';
import { absoluteHrefForCommands, navigateFromPointerEvent } from '../../../shared/utils/in-app-navigation.util';

// Import types from their respective modules
import { MoreActionsItem } from '../../../shared/components/datatable/more-actions-menu/more-actions-menu.models';
import { FloatingToolbarAction } from '../../../core/models/floating-toolbar.models';
import { CommonModule } from '@angular/common';
import { ConfirmationModalComponent, ConfirmationModalConfig, ConfirmationModalEvent } from '../../../shared/components/confirmation-modal';
import { ActionFeedbackService } from '../../../core/services/action-feedback.service';
import { formatApiCalendarDateNumeric } from '../../../shared/utils/date.util';
import { RoleReassignmentModalComponent } from '../../../shared/components/role-reassignment-modal/role-reassignment-modal.component';

@Component({
  selector: 'app-user-roles',
  standalone: true,
  imports: [CommonModule, PrimeNgDataTableComponent, ConfirmationModalComponent, RoleReassignmentModalComponent],
  templateUrl: './user-roles.component.html',
  styleUrls: ['./user-roles.component.scss']
})
export class UserRolesComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(PrimeNgDataTableComponent) datatableComponent!: PrimeNgDataTableComponent;

  // Pagination and state
  currentPage = 1;
  currentFilter = '';
  currentSearchTerm = '';
  currentSortColumn = 'roleTitle';
  currentSortDirection: 'asc' | 'desc' = 'asc';
  currentStatusFilter = '';
  currentDateStart = '';
  currentDateEnd = '';

  // Data - converted to signals for PrimeNG datatable
  roles = signal<any[]>([]);
  totalEntries = signal<number>(0);
  hasNextPage = signal<boolean>(true);
  accumulatedRoles = signal<any[]>([]);
  loading = signal<boolean>(false);

  /** Total role count for the admin page header (toolbar is rendered in parent). */
  readonly rolesTotalChange = output<number>();

  // PrimeNG datatable specific properties
  selectedRows = signal<number[]>([]);
  filterOptions: { [key: string]: any } = {};

  // Confirmation modal properties
  isDeleteModalOpen = false;
  deleteModalConfig: ConfirmationModalConfig = {
    icon: '',
    title: '',
    description: '',
    confirmButtonText: ''
  };
  roleToDelete: any = null;
  rolesToDelete: any[] = []; // For batch deletion
  isDeletingRole = false;

  // Role reassignment modal properties
  showRoleReassignmentModal = false;
  availableRolesForReassignment: any[] = [];

  // Request tracking to prevent race conditions
  private currentDeleteRequest: any = null;

  // Track the currently selected row for the more actions menu
  selectedRowData: any = null;

  // Table configuration - converted to computed signal for PrimeNG datatable
  tableColumns = computed((): DatatableColumn[] => [
    {
      key: 'roleTitle',
      label: 'Role title',
      sortable: true,
      width: '24%',
      minWidth: '160px',
      visible: true,
      cssClass: 'user-roles-col-title'
    },
    {
      key: 'description',
      label: 'Description',
      sortable: false,
      width: '40%',
      minWidth: '200px',
      visible: true,
      cssClass: 'user-roles-col-description'
    },
    {
      key: 'userCount',
      label: 'No. of users',
      sortable: true,
      width: '120px',
      minWidth: '120px',
      visible: true,
      cssClass: 'user-roles-col-count'
    },
    {
      key: 'actions',
      label: '',
      type: 'action',
      width: '0.5rem',
      minWidth: '0.5rem',
      sortable: false,
      visible: true
    }
  ]);

  readonly tableOptions: DatatableOptions = {
    pageSize: 20,
    lazyLoading: true,
    searchable: true,
    sortable: true,
    showColumnManagement: false,
    showMoreActions: false,
    showSearch: false, // Search is in the admin page header
    showFilter: false,
    showColumnVisibility: false,
    showFloatingToolbar: false // User roles doesn't need floating toolbar
  };

  // No filters for user roles table - keeping it simple
  readonly dataFilters: DataFilterConfig[] = [];

  // No text filters for user roles table
  readonly textFilters: TextFilterConfig[] = [];

  // More actions for individual rows - will be overridden to be dynamic
  get moreActions(): MoreActionsItem[] {
    if (this.selectedRowData) {
      const isCore = this.selectedRowData.isCore || false;
      return [
        {
          label: 'Edit',
          icon: '../assets/icons/edit.svg',
          action: 'edit'
        },
        {
          label: 'Delete',
          icon: '../assets/icons/delete.svg',
          action: 'delete',
          disabled: isCore // Disable delete for core roles
        }
      ];
    }

    return [
      {
        label: 'Edit',
        icon: '../assets/icons/edit.svg',
        action: 'edit'
      },
      {
        label: 'Delete',
        icon: '../assets/icons/delete.svg',
        action: 'delete'
      }
    ]
  };

  // Method to get dynamic more actions for a specific role
  getMoreActionsForRole(role: any): MoreActionsItem[] {
    return [
      {
        label: 'Edit',
        icon: '../assets/icons/edit.svg',
        action: 'edit'
      },
      {
        label: 'Delete',
        icon: '../assets/icons/delete.svg',
        action: 'delete',
        disabled: role.isCore // Disable delete for core roles
      }
    ];
  }

  // Floating toolbar actions for batch operations
  readonly floatingToolbarActions: FloatingToolbarAction[] = [];

  roleRowDetailHref = (row: { roleId: number }): string | null => {
    if (row?.roleId == null) {
      return null;
    }
    const commands = buildRouteWithTenant(this.router, ['admin', 'roles', 'edit-role', String(row.roleId)]);
    return absoluteHrefForCommands(this.router, commands);
  };

  // Label filter configuration for role filtering - removed, using new text-based filter system
  labelFilterConfig: LabelFilterConfig | null = null;

  constructor(
    private roleService: RoleService,
    private router: Router,
    private actionFeedback: ActionFeedbackService,
    private permissionService: PermissionService
  ) { }

  ngOnInit() {
    this.loadRolesFromAPI(false);
  }

  ngAfterViewInit() {
    // PrimeNG datatable doesn't need the same initialization as the old datatable
  }

  ngOnDestroy() {
    // Cleanup if needed
  }

  private loadRolesFromAPI(isLazyLoad: boolean = false) {
    this.loading.set(true);

    const request: PaginatedRolesRequest = {
      pageNumber: this.currentPage,
      pageSize: this.tableOptions.pageSize || 20,
      sortBy: this.currentSortColumn,
      sortOrder: this.currentSortDirection,
      searchTerm: this.currentSearchTerm || undefined
    };

    this.roleService.getPaginatedRoles(request).subscribe({
      next: (response: PaginatedRolesResponse) => {
        if (response?.success && response?.data) {
          const transformedRoles = response.data.items.map((role: any) => this.transformRoleForDisplay(role));

          if (isLazyLoad) {
            this.accumulatedRoles.update(roles => [...roles, ...transformedRoles]);

            // Update PrimeNG datatable component by appending data
            if (this.datatableComponent) {
              this.datatableComponent.addMoreData(transformedRoles);
            } else {
              console.error('❌ PrimeNG datatable component not found when trying to add more data');
            }
          } else {
            this.accumulatedRoles.set(transformedRoles);
            this.datatableComponent.updateLoadedData(transformedRoles, response.data.totalItems);
          }

          this.roles.set(this.accumulatedRoles());
          this.totalEntries.set(response.data.totalItems);
          this.rolesTotalChange.emit(response.data.totalItems);
          this.hasNextPage.set(response.data.hasNextPage);
        } else {
          console.error('❌ Response missing success or data');
        }
        this.loading.set(false);
      },
      error: (error: any) => {
        console.error('❌ Error loading roles:', error);
        this.loading.set(false);
        this.handleApiError();
      }
    });
  }

  private handleApiError() {
    // Handle API errors - could show toast notification
    console.error('❌ API Error occurred');

    // Reset lazy loading state on error to prevent stuck loading state
    if (this.datatableComponent) {
      // PrimeNG datatable handles lazy loading internally
    }
  }

  private transformRoleForDisplay(role: any): any {
    return {
      id: role.roleId, // Add id property for datatable duplicate detection
      roleId: role.roleId,
      roleTitle: role.name,
      description: role.description,
      userCount: role.userCount,
      dateCreated: role.dateCreated,
      dateModified: role.dateModified,
      isDeleted: role.isDeleted,
      isCore: role.isCore, // Include the isCore property
      // Add computed properties for display
      displayRoleTitle: role.name,
      displayDescription: role.description,
      displayUserCount: `${role.userCount} users`,
      displayDateCreated: this.formatDate(role.dateCreated),
      displayDateModified: this.formatDate(role.dateModified)
    };
  }

  private formatDate(dateString: string): string {
    if (!dateString) return '';
    return formatApiCalendarDateNumeric(dateString) || '';
  }

  onTableEvent(event: DatatableEvent) {
    switch (event.type) {
      case 'add':
        this.onAddNewRole();
        break;
      case 'splitButtonItem':
        // Handle split button actions
        if (event.value && event.value.action) {
          this.onSplitButtonAction(event.value.action);
        }
        break;
      case 'loadMore':
        this.handleLoadMore(event.value);
        break;
      case 'sort':
        this.handleSort(event.value);
        break;
      case 'search':
        this.handleSearch(event.value);
        break;
      case 'filter':
        this.handleFilterChange(event.value);
        break;
      case 'clearFilters':
        this.handleClearFilters();
        break;
      case 'rowAction':
        this.onMoreActionClick({action: event.value.action, rowData: event.value.rowData});
        break;
      case 'moreActionsMenuOpen':
        this.onMoreActionClick({action: 'rowSelected', rowData: event.value});
        break;
      default:
        break;
    }
  }

  // PrimeNG datatable event handlers
  handleManualLazyLoad(event: LazyLoadEvent): void {
    this.handleLoadMore({ currentCount: this.roles().length, pageSize: this.tableOptions.pageSize || 20 });
  }

  onRowSelect(event: any): void {
    const roleId = event.data?.roleId;
    if (roleId) {
      this.selectedRows.set([roleId]);
      this.selectedRowData = event.data;
    }
  }

  onRowUnselect(event: any): void {
    this.selectedRows.set([]);
    this.selectedRowData = null;
  }

  onSelectAll(event: any): void {
    // Not used for single select
  }

  getSelectedRowsArray(): number[] {
    return this.selectedRows();
  }

  onRowClick(event: { row: { roleId: number }; event?: MouseEvent }): void {
    if (event?.row?.roleId) {
      const commands = buildRouteWithTenant(this.router, [
        'admin',
        'roles',
        'edit-role',
        event.row.roleId.toString()
      ]);
      navigateFromPointerEvent(event.event, this.router, commands);
    }
  }

  handleRowExpand(event: any): void {
    // Not used for user roles
  }

  private handleLoadMore(event: any) {
    const { currentCount, pageSize } = event;

    // Check if there are more pages to load
    if (!this.hasNextPage()) {
      return;
    }

    // Increment the current page for the next load
    this.currentPage++;
    this.loadRolesFromAPI(true);
  }

  private handleSort(event: any) {
    const { sortField, sortOrder } = event;

    // Map frontend column keys to backend sort keys
    const sortKeyMap: { [key: string]: string } = {
      'roleTitle': 'name',
      'description': 'description',
      'userCount': 'userCount'
    };

    // Update the component's sort state to maintain consistency
    this.currentSortColumn = sortKeyMap[sortField] || sortField;
    this.currentSortDirection = sortOrder === 1 ? 'asc' : 'desc';
    this.currentPage = 1;

    // Reset accumulated roles since we're starting fresh
    this.accumulatedRoles.set([]);
    this.roles.set([]);

    // Reset the datatable component's lazy loading state
    if (this.datatableComponent) {
      // PrimeNG datatable handles lazy loading internally
    }

    // Reload data with new sort parameters
    this.loadRolesFromAPI(false);
  }

  private handleFilterChange(filterValue: string) {
    this.currentFilter = filterValue;
    this.currentPage = 1;

    // Reset accumulated roles since we're starting fresh
    this.accumulatedRoles.set([]);
    this.roles.set([]);

    // Reset the datatable component's lazy loading state
    if (this.datatableComponent) {
      // PrimeNG datatable handles lazy loading internally
    }

    this.loadRolesFromAPI(false);
  }

  private handleSearch(searchTerm: string) {
    this.currentSearchTerm = searchTerm;
    this.currentPage = 1;

    // Reset accumulated roles since we're starting fresh
    this.accumulatedRoles.set([]);
    this.roles.set([]);

    // Reset the datatable component's lazy loading state
    if (this.datatableComponent) {
      // PrimeNG datatable handles lazy loading internally
    }

    // Reload data with new search term
    this.loadRolesFromAPI(false);
  }

  private handleClearFilters() {
    this.currentFilter = '';
    this.currentPage = 1;

    // Reset accumulated roles since we're starting fresh
    this.accumulatedRoles.set([]);
    this.roles.set([]);

    // Reset the datatable component's lazy loading state
    if (this.datatableComponent) {
      // PrimeNG datatable handles lazy loading internally
    }

    this.loadRolesFromAPI(false);
  }

  onToolbarActionClick(event: { action: string; selectedRows: any[] }) {
    switch (event.action) {
      case 'delete':
        // Check if any selected roles are core roles
        const coreRoles = event.selectedRows.filter(role => role.isCore);
        if (coreRoles.length > 0) {
          const coreRoleNames = coreRoles.map(role => role.roleTitle).join(', ');
          this.actionFeedback.showWarning(
            `Cannot delete core roles: ${coreRoleNames}. Core roles are essential system roles and cannot be deleted.`,
            { allowWrap: true }
          );
          return;
        }

        // Show confirmation for all selected non-core roles
        this.showBatchDeleteConfirmation(event.selectedRows);
        break;
    }
  }

  private showBatchDeleteConfirmation(selectedRoles: any[]) {
    // Filter out core roles from batch deletion
    const nonCoreRoles = selectedRoles.filter(role => !role.isCore);

    if (nonCoreRoles.length === 0) {
      this.actionFeedback.showWarning(
        'No non-core roles selected for deletion. Core roles cannot be deleted.'
      );
      return;
    }

    if (nonCoreRoles.length < selectedRoles.length) {
      const coreRoleCount = selectedRoles.length - nonCoreRoles.length;
      this.actionFeedback.showInfo(
        `${coreRoleCount} core role(s) were automatically excluded from deletion as they cannot be deleted.`
      );
    }

    this.rolesToDelete = nonCoreRoles;
    this.roleToDelete = null; // Clear single role deletion

    this.deleteModalConfig = {
      title: 'Delete Selected Roles',
      subtitle: `This action will permanently remove ${nonCoreRoles.length} selected role(s) and their permissions.\n\nAre you sure you want to continue?`,
      description: '',
      showInfoWarning: false,
      aioDestructive: true,
      confirmButtonText: 'Yes, Delete'
    };
    this.isDeleteModalOpen = true;
  }

  onToolbarUndo(event: { action: string; selectedRows: any[] }) {
    // Handle undo action
  }

  onDataFilterChange(filterValues: { [key: string]: any }) {
    // Reload data with new filters
    this.currentPage = 1;

    // Reset accumulated roles since we're starting fresh
    this.accumulatedRoles.set([]);
    this.roles.set([]);

    // Reset the datatable component's lazy loading state
    if (this.datatableComponent) {
      // PrimeNG datatable handles lazy loading internally
    }

    this.loadRolesFromAPI(false);
  }

  onMoreActionClick(event: { action: string; rowData: any }) {
    switch (event.action) {
      case 'rowSelected':
        // Update the selected row data for dynamic more actions
        this.selectedRowData = event.rowData;
        break;
      case 'edit':
        this.editRole(event.rowData);
        this.clearSelectedRowData();
        break;
      case 'delete':
        // Show confirmation modal for deletion
        this.showDeleteConfirmation(event.rowData);
        this.clearSelectedRowData();
        break;
    }
  }

  private clearSelectedRowData(): void {
    this.selectedRowData = null;
    this.selectedRows.set([]);
  }

  /** Called from admin shell header search (toolbar is outside this component). */
  applyToolbarSearch(term: string): void {
    this.handleSearch(term);
  }

  onAddNewRole() {
    // Get current URL to determine tenant ID
    const currentUrl = this.router.url;
    const urlParts = currentUrl.split('/');

    // Check if we have a tenant ID
    if (urlParts.length > 1 && urlParts[1] !== 'admin') {
      // We have a tenant ID, use tenant-specific route
      const tenantId = urlParts[1];
      const targetRoute = `/${tenantId}/admin/roles/add-role`;
      this.router.navigate([targetRoute]);
    } else {
      // No tenant ID, redirect to login
      this.router.navigate(['/login']);
    }
  }

  private showDeleteConfirmation(role: any) {
    this.roleToDelete = role;

    if (role.userCount > 0) {
      // Show confirmation for roles with users
      this.deleteModalConfig = {
        title: "You're About to Delete a Role",
        subtitle: `Before deleting this role, you need to assign its ${role.userCount} user(s) to a different role.\n\nPlease select a new role for all users currently assigned to this role. Once reassigned, this role will be permanently deleted.\n\nAre you sure you want to continue?`,
        description: '',
        showInfoWarning: false,
        aioDestructive: true,
        confirmButtonText: 'Go Select a New Role'
      };
    } else {
      // Show simple confirmation for roles without users
      this.deleteModalConfig = {
        title: "Confirm Deletion of Role",
        subtitle: `This action will permanently remove the selected role and its permissions.\n\nAre you sure you want to continue?`,
        description: '',
        showInfoWarning: false,
        aioDestructive: true,
        confirmButtonText: "Yes, Delete"
      };
    }

    this.isDeleteModalOpen = true;
  }

  private showReassignmentModal() {
    // Load available roles for reassignment
    this.roleService.getRolesForDropdown().subscribe({
      next: (roles: any[]) => {
        // Filter out the role being deleted and roles with 0 users
        this.availableRolesForReassignment = roles.filter((role: any) =>
          role.roleId !== this.roleToDelete.roleId && role.name !== this.roleToDelete.roleTitle
        );

        // Set up the reassignment modal
        this.showRoleReassignmentModal = true;
        this.isDeleteModalOpen = false;
      },
      error: (error: any) => {
        console.error('❌ Error loading roles for reassignment:', error);
      }
    });
  }

  onReassignmentConfirm(event: { roleId: number; targetRoleId: number }) {
    if (!this.roleToDelete || !event.targetRoleId) {
      console.error('❌ Missing role or target role ID');
      return;
    }

    // Store role data locally to prevent null reference in error handler
    const roleToDelete = { ...this.roleToDelete };
    const targetRoleId = event.targetRoleId;

    // Prevent multiple calls by disabling the button or setting a flag
    if (this.isDeletingRole || this.currentDeleteRequest) {
      return;
    }

    // Set flag immediately to prevent race conditions
    this.isDeletingRole = true;

    // Track the current request to prevent race conditions
    this.currentDeleteRequest = this.roleService.deleteRole(roleToDelete.roleId, targetRoleId);

    // Add timeout to reset flags if request takes too long (30 seconds)
    const timeoutId = setTimeout(() => {
      if (this.isDeletingRole) {
        console.warn('⚠️ Delete request timeout, resetting flags');
        this.isDeletingRole = false;
        this.currentDeleteRequest = null;
        this.actionFeedback.showError('Request timed out. Please try again.');
      }
    }, 30000);

    // Use the delete API which handles reassignment automatically
    this.currentDeleteRequest.subscribe({
      next: (response: DeleteRoleResponse) => {
        // Clear timeout
        clearTimeout(timeoutId);

        // Check if the operation was actually successful
        if (response.success) {
          // Close modal
          this.showRoleReassignmentModal = false;
          this.roleToDelete = null;
          this.isDeletingRole = false;
          this.currentDeleteRequest = null;

          this.actionFeedback.showSuccess('1 Role Deleted');

          setTimeout(() => {
            // Navigate to the role details page of the target role (the new role assigned to users)
            this.navigateToRoleDetails(targetRoleId);
          }, 1000); // Wait 1 second for toolbar to be visible
        } else {
          // Operation failed
          console.error('❌ Role deletion failed:', response.message);
          this.isDeletingRole = false;
          this.currentDeleteRequest = null;

          // Show error toast
          this.actionFeedback.showError(response.message || 'Failed to delete role. Please try again.');
        }
      },
      error: (error: any) => {
        // Clear timeout
        clearTimeout(timeoutId);

        console.error('❌ Error deleting role with reassignment:', error);
        console.error('❌ Role that failed to delete:', roleToDelete);
        console.error('❌ Target role ID:', targetRoleId);

        // Close modal and reset state
        this.showRoleReassignmentModal = false;
        this.isDeletingRole = false;
        this.currentDeleteRequest = null;

        // Show error toast for genuine errors (not handled by role service)
        this.actionFeedback.showError('Failed to delete role. Please try again.');

        // The role service should handle successful responses even with error status
        // If we reach here, it's a genuine error that couldn't be recovered
      }
    });
  }

  onReassignmentCancel() {
    this.showRoleReassignmentModal = false;

    // Simply close the modal and stay on the current page
    this.roleToDelete = null;
    this.clearSelectedRowData();
  }

  onDeleteConfirm(event: ConfirmationModalEvent) {
    // Handle batch deletion
    if (this.rolesToDelete && this.rolesToDelete.length > 0) {
      this.handleBatchDelete();
      return;
    }

    // Handle single role deletion
    if (!this.roleToDelete) return;

    if (this.roleToDelete.userCount > 0) {
      // Show reassignment modal for roles with users
      // Don't call delete API here - it will be called in onReassignmentConfirm
      this.showReassignmentModal();
    } else {
      // Direct deletion for roles without users - use delete API without targetRoleId

      // Prevent multiple calls
      if (this.isDeletingRole) {
        return;
      }

      // Set flag immediately to prevent race conditions
      this.isDeletingRole = true;

      // Also close the modal immediately to prevent multiple clicks
      this.isDeleteModalOpen = false;

      // Store role data locally to prevent null reference in error handler
      const roleToDelete = { ...this.roleToDelete };

             this.roleService.deleteRole(roleToDelete.roleId).subscribe({
         next: (response: DeleteRoleResponse) => {
           // Check if the operation was actually successful
           if (response.success) {
             this.isDeletingRole = false;
             this.isDeleteModalOpen = false;
             this.roleToDelete = null;

             // Reset to page 1 since we're starting fresh
             this.currentPage = 1;

             // Reset accumulated roles since we're starting fresh
             this.accumulatedRoles.set([]);
             this.roles.set([]);

             // Reset the datatable component's lazy loading state
             if (this.datatableComponent) {
               // PrimeNG datatable handles lazy loading internally
             }

             // Refresh the roles data to update user counts
             this.loadRolesFromAPI(false);

             this.actionFeedback.showSuccess('1 Role Deleted');
           } else {
             // Operation failed
             console.error('❌ Role deletion failed:', response.message);
             this.isDeletingRole = false;

             // Show error toast
             this.actionFeedback.showError(response.message || 'Failed to delete role. Please try again.');
           }
         },
        error: (error: any) => {
          console.error('❌ Error deleting role:', error);
          console.error('❌ Role that failed to delete:', roleToDelete);
          this.isDeletingRole = false;
        }
      });
    }
  }

  private handleBatchDelete() {
    // Prevent multiple calls
    if (this.isDeletingRole) {
      return;
    }

    // Set flag immediately to prevent race conditions
    this.isDeletingRole = true;

    // Also close the modal immediately to prevent multiple clicks
    this.isDeleteModalOpen = false;

    // Store roles data locally to prevent null reference in error handler
    const rolesToDelete = [...this.rolesToDelete];

    // Delete roles one by one
    let completedDeletions = 0;
    let failedDeletions = 0;

         rolesToDelete.forEach((role, index) => {
       this.roleService.deleteRole(role.roleId).subscribe({
         next: (response: DeleteRoleResponse) => {
           if (response.success) {
             completedDeletions++;
           } else {
             console.error(`❌ Role ${role.roleTitle} deletion failed:`, response.message);
             failedDeletions++;
           }

          // Check if all deletions are complete
          if (completedDeletions + failedDeletions === rolesToDelete.length) {
            this.isDeletingRole = false;
            this.isDeleteModalOpen = false;
            this.rolesToDelete = [];

            // Reset to page 1 since we're starting fresh
            this.currentPage = 1;

            // Reset accumulated roles since we're starting fresh
            this.accumulatedRoles.set([]);
            this.roles.set([]);

            // Reset the datatable component's lazy loading state
            if (this.datatableComponent) {
              // PrimeNG datatable handles lazy loading internally
            }

            // Refresh the roles data to update user counts
            this.loadRolesFromAPI(false);

            if (failedDeletions === 0) {
              this.actionFeedback.showSuccess(`${completedDeletions} Role(s) Deleted: Users Updated`);
            } else {
              this.actionFeedback.showWarning(
                `${completedDeletions} role(s) deleted successfully, ${failedDeletions} role(s) failed to delete.`,
                { allowWrap: true }
              );
            }
          }
        },
        error: (error: any) => {
          console.error(`❌ Error deleting role ${role.roleTitle}:`, error);
          failedDeletions++;

          // Check if all deletions are complete
          if (completedDeletions + failedDeletions === rolesToDelete.length) {
            this.isDeletingRole = false;

            if (failedDeletions === rolesToDelete.length) {
              // All deletions failed
              this.actionFeedback.showError('Failed to delete any roles.');
            } else {
              this.actionFeedback.showWarning(
                `${completedDeletions} role(s) deleted successfully, ${failedDeletions} role(s) failed to delete.`,
                { allowWrap: true }
              );
            }

            // Reset to page 1 since we're starting fresh
            this.currentPage = 1;

            // Reset accumulated roles since we're starting fresh
            this.accumulatedRoles.set([]);
            this.roles.set([]);

            // Reset the datatable component's lazy loading state
            if (this.datatableComponent) {
              // PrimeNG datatable handles lazy loading internally
            }

            // Refresh the roles data to update user counts
            this.loadRolesFromAPI(false);
          }
        }
      });
    });
  }

  onDeleteCancel(event: ConfirmationModalEvent) {
    this.isDeleteModalOpen = false;

    // Simply close the modal and stay on the current page
    this.roleToDelete = null;
    this.rolesToDelete = [];
    this.clearSelectedRowData();
  }

  onActionResultShown() {
    // Handle action result shown event
  }

  private editRole(role: any) {
    // Navigate to edit role page in edit mode with the role ID
    const roleId = role.roleId;
    if (roleId) {
      const editRoleRoute = buildRouteWithTenant(this.router, ['admin', 'roles', 'edit-role', roleId.toString()]);
      this.router.navigate(editRoleRoute, {
        queryParams: { mode: 'edit' }
      });
    } else {
      console.error('❌ No roleId found in role data:', role);
    }
  }

  onCellClick(event: { event: Event, row: any, column: DatatableColumn }) {
    // Navigate to edit role page in view mode with the role ID
    const roleId = event.row.roleId;
    if (roleId) {
      const editRoleRoute = buildRouteWithTenant(this.router, ['admin', 'roles', 'edit-role', roleId.toString()]);
      this.router.navigate(editRoleRoute);
    } else {
      console.error('❌ No roleId found in row data');
    }
  }

  private navigateToRoleDetails(roleId: number) {
    const editRoleRoute = buildRouteWithTenant(this.router, ['admin', 'edit-role', roleId.toString()]);
    this.router.navigate(editRoleRoute);
  }

  /**
   * Handle split button actions
   */
  onSplitButtonAction(action: string) {
    switch (action) {
      case 'importRoles':
        this.handleImportRoles();
        break;
      default:
        console.warn('Unknown split button action:', action);
    }
  }

  /**
   * Handle import roles action
   */
  private handleImportRoles() {
    // Import functionality - placeholder for now
    // TODO: Implement import roles functionality
  }

  /**
   * Permission checking methods
   */
  hasRolesAddEditPermission(): boolean {
    return this.permissionService.hasAnyPermissionSync([SYSTEM_PERMISSION_LIST.ROLES_ADD_EDIT]);
  }
}
